import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Mail, Building, Calendar, User, Send, Clock, CheckCircle, AlertCircle, RefreshCw, Target, Euro } from 'lucide-react';

interface CallRequest {
  id: string;
  promoterName: string;
  promoterCompany: string;
  promoterAvatar: string;
  requestDate: string;
  status: string;
  emailSent: boolean;
  reminderCount: number;
  lastReminder: string | null;
  product: string;
  conversionValue: number;
  notes: string;
  type: string;
}

const OneToOneCallRequestDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [isResending, setIsResending] = useState(false);

  const request: CallRequest = {
    id: id || '1',
    promoterName: 'Jean Martin',
    promoterCompany: 'Startup Solutions',
    promoterAvatar: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=150',
    requestDate: '2024-01-10',
    status: 'accepted',
    emailSent: true,
    reminderCount: 0,
    lastReminder: null,
    product: 'Clareefai Pro',
    conversionValue: 25000,
    notes: 'High-value promoter, excellent references',
    type: 'our_request'
  };

  const handleClose = () => {
    navigate('/1to1-calls');
  };

  const handleResendEmail = async () => {
    if (!request) return;

    setIsResending(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    console.log('Resending email');

    setIsResending(false);
  };

  const generateEmailTemplate = () => {
    if (!request) return '';

    return `${t('oneToOneCalls.request.emailTemplate.greeting', { name: request.promoterName })}

${t('oneToOneCalls.request.emailTemplate.intro')}

${t('oneToOneCalls.request.emailTemplate.body', { product: request.product })}

${t('oneToOneCalls.request.emailTemplate.value')}

${t('oneToOneCalls.request.emailTemplate.involveHeading')}
• ${t('oneToOneCalls.request.emailTemplate.involveShort')}
• ${t('oneToOneCalls.request.emailTemplate.involveSharing')}
• ${t('oneToOneCalls.request.emailTemplate.involveAnswering')}
• ${t('oneToOneCalls.request.emailTemplate.involveScheduling')}

${t('oneToOneCalls.request.emailTemplate.provideHeading')}
• ${t('oneToOneCalls.request.emailTemplate.provideProspects')}
• ${t('oneToOneCalls.request.emailTemplate.provideTalking')}
• ${t('oneToOneCalls.request.emailTemplate.provideScheduling')}
• ${t('oneToOneCalls.request.emailTemplate.provideCompensation')}

${t('oneToOneCalls.request.emailTemplate.interest')}

${t('oneToOneCalls.request.emailTemplate.thanks')}

${t('oneToOneCalls.request.emailTemplate.signoff')}
${t('oneToOneCalls.request.emailTemplate.team')}`;
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      sent: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      declined: 'bg-red-100 text-red-800'
    };

    const labelKeys: Record<string, string> = {
      sent: 'oneToOneCalls.status.sent',
      accepted: 'oneToOneCalls.status.accepted',
      pending: 'oneToOneCalls.status.pending',
      declined: 'oneToOneCalls.status.declined'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badges[status as keyof typeof badges]}`}>
        {labelKeys[status] ? t(labelKeys[status]) : status}
      </span>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <Send className="w-5 h-5 text-blue-600" />;
      case 'accepted':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'declined':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Mail className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusHeading = (status: string) => {
    switch (status) {
      case 'sent': return t('oneToOneCalls.request.statusHeadings.sent');
      case 'accepted': return t('oneToOneCalls.request.statusHeadings.accepted');
      case 'pending': return t('oneToOneCalls.request.statusHeadings.pending');
      case 'declined': return t('oneToOneCalls.request.statusHeadings.declined');
      default: return t('oneToOneCalls.request.statusHeadings.unknown');
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'sent': return t('oneToOneCalls.request.statusMessages.sent');
      case 'accepted': return t('oneToOneCalls.request.statusMessages.accepted');
      case 'pending': return t('oneToOneCalls.request.statusMessages.pending');
      case 'declined': return t('oneToOneCalls.request.statusMessages.declined');
      default: return t('oneToOneCalls.request.statusMessages.unknown');
    }
  };

  if (!request) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('oneToOneCalls.request.title')}</h1>
          <p className="text-gray-600 mt-1">{t('oneToOneCalls.request.subtitle')}</p>
        </div>
        <button
          onClick={handleClose}
          className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <X className="w-4 h-4" />
          <span>{t('oneToOneCalls.buttons.back')}</span>
        </button>
      </div>

      <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('oneToOneCalls.request.requestStatus')}</h3>
                {getStatusBadge(request.status)}
              </div>

              <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                  {getStatusIcon(request.status)}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">
                    {getStatusHeading(request.status)}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {getStatusMessage(request.status)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('oneToOneCalls.request.promoterInfo')}</h3>
              <div className="flex items-center space-x-4">
                <img
                  src={request.promoterAvatar}
                  alt={request.promoterName}
                  className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                />
                <div className="flex-1">
                  <h4 className="text-xl font-bold text-gray-900">{request.promoterName}</h4>
                  <p className="text-gray-600 flex items-center mt-1">
                    <Building className="w-4 h-4 mr-1" />
                    {request.promoterCompany}
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      {t('oneToOneCalls.request.requestSent', { date: new Date(request.requestDate).toLocaleDateString('en-US') })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('oneToOneCalls.request.requestDetails')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-medium text-gray-600">{t('oneToOneCalls.fields.product')}:</span>
                  <p className="text-gray-900">{request.product}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t('oneToOneCalls.fields.dealValue')}:</span>
                  <p className="text-green-600 font-bold">€{request.conversionValue.toLocaleString()}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t('oneToOneCalls.fields.requestDate')}:</span>
                  <p className="text-gray-900">{new Date(request.requestDate).toLocaleDateString('en-US')}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t('oneToOneCalls.fields.emailSent')}:</span>
                  <p className="text-gray-900">{request.emailSent ? t('oneToOneCalls.request.emailSentYes') : t('oneToOneCalls.request.emailSentNo')}</p>
                </div>
                {request.reminderCount > 0 && (
                  <>
                    <div>
                      <span className="font-medium text-gray-600">{t('oneToOneCalls.fields.reminders')}:</span>
                      <p className="text-orange-600 font-medium">{request.reminderCount}</p>
                    </div>
                    {request.lastReminder && (
                      <div>
                        <span className="font-medium text-gray-600">{t('oneToOneCalls.fields.lastReminder')}:</span>
                        <p className="text-gray-900">{new Date(request.lastReminder).toLocaleDateString('en-US')}</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {request.notes && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Mail className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-yellow-800 text-sm">{request.notes}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('oneToOneCalls.request.emailTemplateSent')}</h3>
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="bg-white rounded border p-4">
                  <div className="border-b pb-3 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                      <Mail className="w-4 h-4" />
                      <span>{t('oneToOneCalls.request.to', { name: request.promoterName })}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {t('oneToOneCalls.request.subject')}
                    </div>
                  </div>

                  <div className="whitespace-pre-line text-sm text-gray-800 font-mono leading-relaxed max-h-60 overflow-y-auto">
                    {generateEmailTemplate()}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('oneToOneCalls.request.availableActions')}</h3>
              <div className="space-y-3">
                <button
                  onClick={handleResendEmail}
                  disabled={isResending}
                  className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isResending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{t('oneToOneCalls.request.resending')}</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5" />
                      <span>{t('oneToOneCalls.request.resendEmail')}</span>
                    </>
                  )}
                </button>

                <button className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                  <X className="w-5 h-5" />
                  <span>{t('oneToOneCalls.request.cancelRequest')}</span>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('oneToOneCalls.request.timeline')}</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">{t('oneToOneCalls.request.timelineEntries.requestCreated')}</p>
                    <p className="text-xs text-blue-700">{t('oneToOneCalls.request.timelineEntries.requestCreatedDetails', { date: new Date(request.requestDate).toLocaleDateString('en-US'), name: request.promoterName })}</p>
                  </div>
                </div>

                {request.emailSent && (
                  <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-900">{t('oneToOneCalls.request.timelineEntries.emailSent')}</p>
                      <p className="text-xs text-green-700">{t('oneToOneCalls.request.timelineEntries.emailSentDetails')}</p>
                    </div>
                  </div>
                )}

                {request.reminderCount > 0 && request.lastReminder && (
                  <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                    <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-orange-900">{t('oneToOneCalls.request.timelineEntries.reminderSent', { count: request.reminderCount })}</p>
                      <p className="text-xs text-orange-700">{t('oneToOneCalls.request.timelineEntries.reminderSentDetails', { date: new Date(request.lastReminder).toLocaleDateString('en-US') })}</p>
                    </div>
                  </div>
                )}

                {request.status === 'accepted' && (
                  <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                    <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-purple-900">{t('oneToOneCalls.request.timelineEntries.requestAccepted')}</p>
                      <p className="text-xs text-purple-700">{t('oneToOneCalls.request.timelineEntries.requestAcceptedDetails')}</p>
                    </div>
                  </div>
                )}

                {request.status === 'declined' && (
                  <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900">{t('oneToOneCalls.request.timelineEntries.requestDeclined')}</p>
                      <p className="text-xs text-red-700">{t('oneToOneCalls.request.timelineEntries.requestDeclinedDetails')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
      </div>
    </div>
  );
};

export default OneToOneCallRequestDetails;
